import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * BillingModal — Admin-only "Charge customer" flow.
 * Lists saved cards from `/api/admin/billing/customers`.
 * Charge form uses appointment_count × rate → amount, auto-invoice #, optional receipt email.
 */
export default function BillingModal({ onClose }) {
  const [tab, setTab] = useState("list"); // "list" | "charge" | "done"
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await axios.get(`${API}/admin/billing/customers`);
      setCustomers(res.data.customers || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load customers");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query) return customers;
    const q = query.toLowerCase();
    return customers.filter((c) => [c.email, c.first_name, c.last_name, c.business_name]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [customers, query]);

  const pickCustomer = (c) => { setSelected(c); setTab("charge"); };
  const backToList = () => { setSelected(null); setTab("list"); };
  const onCharged = (result) => { setLastResult(result); setTab("done"); load(); };

  return (
    <div style={S.backdrop} onClick={onClose} data-testid="billing-modal">
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div>
            <div style={S.eyebrow}>Admin · Billing</div>
            <div style={S.title}>
              {tab === "list" && "Charge a customer"}
              {tab === "charge" && `Charge ${selected?.first_name || selected?.email}`}
              {tab === "done" && "Charged"}
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose} data-testid="billing-close">✕</button>
        </div>

        <div style={S.body}>
          {tab === "list" && (
            <CustomerList
              loading={loading} error={err} customers={filtered} query={query}
              onQuery={setQuery} onPick={pickCustomer} onRetry={load}
            />
          )}
          {tab === "charge" && selected && (
            <ChargeForm customer={selected} onBack={backToList} onCharged={onCharged} />
          )}
          {tab === "done" && lastResult && (
            <ChargeSuccess result={lastResult} onNew={backToList} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CUSTOMER LIST
// ═══════════════════════════════════════════════════════════════════════
function CustomerList({ loading, error, customers, query, onQuery, onPick, onRetry }) {
  return (
    <>
      <input
        type="text" value={query} onChange={(e) => onQuery(e.target.value)}
        placeholder="Search email, name, business…" style={S.search}
        data-testid="billing-search"
      />
      {loading && <div style={S.info}>Loading customers…</div>}
      {error && !loading && (
        <div style={S.error}>
          {error} <button onClick={onRetry} style={S.textBtn}>Retry</button>
        </div>
      )}
      {!loading && !error && customers.length === 0 && (
        <div style={S.empty}>
          <div style={S.emptyIcon}>💳</div>
          <div style={S.emptyTitle}>No saved cards yet</div>
          <div style={S.emptySub}>Customers who complete a `/onboard/split-2` or `/onboard/split-3` checkout will appear here — you can then charge them any amount, any time.</div>
        </div>
      )}
      <div style={S.rows}>
        {customers.map((c) => (
          <button key={c.email} onClick={() => onPick(c)} style={S.row} data-testid={`billing-row-${c.email}`}>
            <div style={S.rowLeft}>
              <div style={S.rowName}>{c.first_name} {c.last_name} <span style={S.rowBiz}>· {c.business_name || "—"}</span></div>
              <div style={S.rowMeta}>{c.email} · {c.phone || "no phone"}</div>
            </div>
            <div style={S.rowRight}>
              <div style={S.rowLast}>Last paid</div>
              <div style={S.rowLastVal}>{c.last_payment_at ? new Date(c.last_payment_at).toLocaleDateString() : "—"}</div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CHARGE FORM
// ═══════════════════════════════════════════════════════════════════════
function ChargeForm({ customer, onBack, onCharged }) {
  const defaultRange = useMemo(() => defaultPrevWeek(), []);
  const [clientName, setClientName] = useState(
    `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || customer.business_name || customer.email.split("@")[0],
  );
  const [appointmentCount, setAppointmentCount] = useState("");
  const [rate, setRate] = useState(97);
  const [weekStart, setWeekStart] = useState(defaultRange.start);
  const [weekEnd, setWeekEnd] = useState(defaultRange.end);
  const [description, setDescription] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apptNum = Number(appointmentCount || 0);
  const amount = apptNum > 0 ? +(apptNum * rate).toFixed(2) : 0;
  const canCharge = amount > 0 && !busy;
  const autoDescription = apptNum > 0
    ? `Ads management — ${apptNum} appointments (week ${weekStart} → ${weekEnd})`
    : "Skye retainer";

  const submit = async (e) => {
    e.preventDefault();
    if (!canCharge) return;
    setBusy(true); setErr("");
    try {
      const res = await axios.post(`${API}/admin/billing/charge`, {
        email: customer.email,
        client_name: clientName.trim() || undefined,
        appointment_count: apptNum,
        rate_per_appointment: rate,
        week_start: weekStart || undefined,
        week_end: weekEnd || undefined,
        description: description.trim() || undefined,
        internal_notes: internalNotes.trim() || undefined,
        send_email: sendEmail,
        idempotency_key: `${customer.email}-${weekStart}-${weekEnd}-${apptNum}-${Date.now()}`,
      });
      onCharged({ ...res.data, client_name: clientName, appointment_count: apptNum, week_start: weekStart, week_end: weekEnd, description: description.trim() || autoDescription });
    } catch (e) {
      setErr(e.response?.data?.detail || "Charge failed. Try again.");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} style={S.form} data-testid="billing-charge-form">
      <button type="button" onClick={onBack} style={S.backBtn} data-testid="billing-back">← Pick a different customer</button>

      <div style={S.custCard}>
        <div style={S.custName}>{customer.first_name} {customer.last_name}</div>
        <div style={S.custMeta}>{customer.email} · {customer.business_name || "—"}</div>
        <div style={S.custMeta}>Saved card: <b>{customer.whop_payment_method_id ? "✓ Reusable" : "—"}</b></div>
      </div>

      <div style={S.sectionLabel}>Charge details</div>

      <div style={S.field}>
        <label style={S.label}>Client's name (shown on receipt)</label>
        <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Alex Jordan" style={S.input} data-testid="billing-client-name" />
      </div>

      <div style={S.row2}>
        <div style={{ ...S.field, flex: 2 }}>
          <label style={S.label}>Number of appointments</label>
          <input type="number" min="1" max="500" value={appointmentCount} onChange={(e) => setAppointmentCount(e.target.value)} placeholder="e.g. 5" style={S.input} data-testid="billing-appt-count" />
        </div>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>Rate ($)</label>
          <input type="number" min="1" value={rate} onChange={(e) => setRate(Number(e.target.value) || 0)} style={S.input} data-testid="billing-rate" />
        </div>
      </div>

      <div style={S.row2}>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>Week start (Sun)</label>
          <input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} style={S.input} data-testid="billing-week-start" />
        </div>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>Week end (Sat)</label>
          <input type="date" value={weekEnd} onChange={(e) => setWeekEnd(e.target.value)} style={S.input} data-testid="billing-week-end" />
        </div>
      </div>

      <div style={S.field}>
        <label style={S.label}>Description (optional — auto-fills)</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={autoDescription} style={S.input} data-testid="billing-description" />
      </div>

      <div style={S.field}>
        <label style={S.label}>Internal notes (admin only, not on receipt)</label>
        <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} placeholder="e.g. campaign name, ad-set ref, anything for our records" style={{ ...S.input, minHeight: 64, resize: "vertical", fontFamily: "inherit" }} data-testid="billing-notes" />
      </div>

      <label style={S.checkboxRow} data-testid="billing-send-email-row">
        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="billing-send-email" />
        <span>Email the customer a branded receipt</span>
      </label>

      {/* Totals summary */}
      <div style={S.totalsBox} data-testid="billing-totals">
        <div style={S.totalsRow}>
          <span style={S.totalsLabel}>Appointments</span>
          <span>{apptNum || 0} × ${rate.toLocaleString()}</span>
        </div>
        <div style={{ ...S.totalsRow, marginTop: 10, borderTop: "1px dashed rgba(255,255,255,.15)", paddingTop: 10 }}>
          <span style={S.totalsLabel}>Total to charge</span>
          <b style={S.totalsGrand}>${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
        </div>
      </div>

      {err && <div style={S.error}>{err}</div>}

      <button type="submit" disabled={!canCharge} style={{ ...S.primaryBtn, opacity: canCharge ? 1 : 0.45 }} data-testid="billing-submit">
        {busy ? "Charging…" : amount > 0 ? `Charge $${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} to ${customer.email}` : "Enter appointments to charge"}
      </button>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUCCESS
// ═══════════════════════════════════════════════════════════════════════
function ChargeSuccess({ result, onNew, onClose }) {
  return (
    <div style={S.success} data-testid="billing-success">
      <div style={S.successBadge}>✓</div>
      <h2 style={S.successTitle}>Charge submitted</h2>
      <p style={S.successSub}>
        {result.email_sent
          ? `Receipt emailed to ${result.email}. Final status confirms when Whop settles the payment.`
          : `Receipt was NOT sent by email. Final status confirms when Whop settles the payment.`}
      </p>
      <div style={S.summaryGrid}>
        <div style={S.summaryRow}><span>Receipt #</span><b>{result.invoice_no}</b></div>
        <div style={S.summaryRow}><span>Amount</span><b>${Number(result.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
        <div style={S.summaryRow}><span>To</span><b>{result.client_name || result.email}</b></div>
        <div style={S.summaryRow}><span>Description</span><b style={{ textAlign: "right", maxWidth: "60%" }}>{result.description}</b></div>
        {result.week_start && <div style={S.summaryRow}><span>Week</span><b>{result.week_start} → {result.week_end}</b></div>}
        <div style={S.summaryRow}><span>Status</span><b style={{ color: "#F472B6" }}>{result.status || "created"}</b></div>
        {result.payment_id && <div style={S.summaryRow}><span>Whop ID</span><b style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11 }}>{result.payment_id}</b></div>}
      </div>
      <div style={S.actionRow}>
        <button onClick={onNew} style={S.secondaryBtn} data-testid="billing-new">Charge another</button>
        <button onClick={onClose} style={S.primaryBtn} data-testid="billing-done">Done</button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────
function defaultPrevWeek() {
  // Return { start: "YYYY-MM-DD" Sunday, end: "YYYY-MM-DD" Saturday } for the PREVIOUS Sun→Sat.
  const today = new Date();
  const day = today.getDay(); // 0=Sun ... 6=Sat
  // Last Saturday = today − (day + 1)
  const lastSat = new Date(today); lastSat.setDate(today.getDate() - (day + 1));
  const lastSun = new Date(lastSat); lastSun.setDate(lastSat.getDate() - 6);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(lastSun), end: fmt(lastSat) };
}

// ─── Styles ─────────────────────────────────────────────────────────
const S = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(3,3,10,0.78)", backdropFilter: "blur(6px)", zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal: { width: "100%", maxWidth: 640, maxHeight: "92vh", overflow: "hidden", background: "linear-gradient(180deg,#0c0c17,#07070f)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 20, boxShadow: "0 30px 80px -20px rgba(0,0,0,.8)", display: "flex", flexDirection: "column", color: "#fff", fontFamily: "'Manrope',system-ui,sans-serif" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 22px", borderBottom: "1px solid rgba(255,255,255,.06)" },
  eyebrow: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: "#F472B6", textTransform: "uppercase", marginBottom: 4 },
  title: { fontSize: 20, fontWeight: 800, letterSpacing: "-.01em" },
  closeBtn: { width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.12)", color: "rgba(255,255,255,.7)", fontSize: 16, cursor: "pointer" },
  body: { flex: 1, overflow: "auto", padding: "18px 22px 22px" },
  search: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 12, color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none", marginBottom: 12 },
  info: { padding: "16px", color: "rgba(255,255,255,.6)", textAlign: "center", fontSize: 13 },
  error: { padding: "10px 12px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, color: "#FCA5A5", fontSize: 13, margin: "10px 0" },
  empty: { padding: "40px 24px", textAlign: "center", border: "1px dashed rgba(255,255,255,.12)", borderRadius: 14 },
  emptyIcon: { fontSize: 32, marginBottom: 10 },
  emptyTitle: { fontSize: 15, fontWeight: 700, color: "rgba(255,255,255,.85)", marginBottom: 6 },
  emptySub: { fontSize: 12.5, color: "rgba(255,255,255,.5)", lineHeight: 1.5, maxWidth: 460, margin: "0 auto" },
  rows: { display: "flex", flexDirection: "column", gap: 6 },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 12, cursor: "pointer", color: "#fff", fontFamily: "inherit", textAlign: "left", transition: "all .15s ease" },
  rowLeft: { minWidth: 0, flex: 1 },
  rowName: { fontSize: 14, fontWeight: 700 },
  rowBiz: { color: "rgba(255,255,255,.5)", fontWeight: 400, fontSize: 13 },
  rowMeta: { fontSize: 12, color: "rgba(255,255,255,.5)", marginTop: 3, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3 },
  rowRight: { textAlign: "right", flexShrink: 0, marginLeft: 12 },
  rowLast: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,.4)", letterSpacing: 1, textTransform: "uppercase" },
  rowLastVal: { fontSize: 12, color: "rgba(255,255,255,.75)", marginTop: 2 },
  form: { display: "flex", flexDirection: "column" },
  backBtn: { alignSelf: "flex-start", background: "transparent", color: "rgba(255,255,255,.55)", border: "none", padding: "4px 0", fontSize: 12, cursor: "pointer", marginBottom: 10, fontFamily: "inherit" },
  custCard: { padding: "12px 14px", background: "linear-gradient(135deg,rgba(220,50,140,.1),rgba(157,50,243,.04))", border: "1px solid rgba(220,50,140,.28)", borderRadius: 12, marginBottom: 16 },
  custName: { fontSize: 15, fontWeight: 800 },
  custMeta: { fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 4, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3 },
  sectionLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.6, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,.06)" },
  field: { marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 },
  row2: { display: "flex", gap: 10 },
  label: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,.55)", textTransform: "uppercase" },
  input: { padding: "12px 14px", fontSize: 14, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, color: "#fff", outline: "none", fontFamily: "inherit" },
  checkboxRow: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "rgba(255,255,255,.75)", marginTop: 4, marginBottom: 8, cursor: "pointer" },
  totalsBox: { padding: "14px 16px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(220,50,140,.32)", borderRadius: 12, marginTop: 10 },
  totalsRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, color: "rgba(255,255,255,.75)" },
  totalsLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: "rgba(255,255,255,.55)", textTransform: "uppercase" },
  totalsGrand: { fontSize: 20, fontWeight: 800, color: "#F472B6" },
  primaryBtn: { padding: "14px 20px", background: "linear-gradient(135deg,#dc328c,#9d32f3)", color: "#fff", fontSize: 15, fontWeight: 800, border: "none", borderRadius: 14, cursor: "pointer", boxShadow: "0 10px 26px -6px rgba(220,50,140,.5)", fontFamily: "inherit", marginTop: 14 },
  secondaryBtn: { padding: "14px 20px", background: "rgba(255,255,255,.05)", color: "rgba(255,255,255,.85)", fontSize: 14, fontWeight: 700, border: "1px solid rgba(255,255,255,.15)", borderRadius: 14, cursor: "pointer", fontFamily: "inherit", marginTop: 14 },
  textBtn: { background: "transparent", color: "#F472B6", border: "none", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 700, marginLeft: 8, fontFamily: "inherit" },
  success: { textAlign: "center", padding: "8px 0" },
  successBadge: { width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg,#4ADE80,#22c55e)", color: "#fff", fontSize: 30, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 0 42px rgba(74,222,128,.4)" },
  successTitle: { fontSize: 24, fontWeight: 800, marginBottom: 8 },
  successSub: { fontSize: 13.5, color: "rgba(255,255,255,.7)", lineHeight: 1.55, margin: "0 auto 20px", maxWidth: 480 },
  summaryGrid: { display: "flex", flexDirection: "column", gap: 8, textAlign: "left", marginBottom: 20 },
  summaryRow: { display: "flex", justifyContent: "space-between", padding: "10px 14px", background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 10, fontSize: 13 },
  actionRow: { display: "flex", gap: 10, justifyContent: "center" },
};

if (typeof document !== "undefined" && !document.getElementById("billing-kf")) {
  const s = document.createElement("style");
  s.id = "billing-kf";
  s.textContent = "input::placeholder,textarea::placeholder{color:rgba(255,255,255,.35)}input:focus,textarea:focus{border-color:rgba(220,50,140,.55) !important}button:not(:disabled):hover{transform:translateY(-1px)}";
  document.head.appendChild(s);
}
